import type { Ctx } from "../art/pixel";
import { $, h } from "../lib/dom";
import { clamp } from "../lib/util";
import { post } from "./log";
import { rt, S } from "./state";
import { idleSecNow } from "./time";
import type { Flag, LogKind, WaitInput } from "./types";
import { entFor, T } from "./world";
import { isZoomOpen } from "./zoom";

/** Set (or clear, with no flag) the bubble over a character. */
export function flag(who: string, f?: Flag): void {
  const e = entFor(who);
  if (!e) return;
  if (f) S.flags[e.id] = f;
  else Reflect.deleteProperty(S.flags, e.id);
}

export function addWait(w: WaitInput): void {
  S.waits = S.waits.filter((x) => x.id !== w.id);
  S.waits.push({ from: S.t, clearOn: "talk", ...w });
  renderWaits();
}

export function clearWait(id: string): void {
  if (!S.live) return;
  S.waits = S.waits.filter((x) => x.id !== id);
  renderWaits();
}

/** NPC asks the clerk something: posts it, shows "?", optionally registers a wait. */
export function ask(ch: string, who: string, text: string, w?: Omit<WaitInput, "who">): void {
  post(ch, "npc", who, text);
  flag(who, "?");
  if (w) addWait({ who, ...w });
}

export function onDialogOpened(id: string): void {
  for (const e of S.ents) if (e.act === id && S.flags[e.id] === "!") Reflect.deleteProperty(S.flags, e.id);
  S.waits = S.waits.filter((w) => !(w.dlg === id && w.clearOn === "open"));
  renderWaits();
}

export function onTalked(id: string): void {
  for (const e of S.ents) if (e.act === id) Reflect.deleteProperty(S.flags, e.id);
  S.waits = S.waits.filter((w) => !(w.dlg === id && w.clearOn === "talk"));
  renderWaits();
}

export function renderWaits(): void {
  const box = $("#hudWaits");
  const zb = $("#zwaits");
  box.replaceChildren();
  zb.replaceChildren();
  if (!S.live || S.cine) return;
  for (const w of S.waits) {
    const d = h("div", { className: "wait" + (w.urgent ? " u" : ""), text: `${w.icon ?? "？"} ${w.text}` }); // i18n-ok: icon glyph
    if (w.untilT !== undefined) {
      const sec = idleSecNow();
      const total = Math.max(1, (w.untilT - w.from) * sec);
      const left = Math.max(0, (w.untilT - S.t) * sec - rt.idle);
      const fill = h("i");
      fill.style.width = `${clamp((left / total) * 100, 0, 100)}%`;
      d.appendChild(h("div", { className: "bar" }, fill));
    }
    box.appendChild(d);
    if (isZoomOpen()) zb.appendChild(d.cloneNode(true));
  }
}

/** Bump a character's impatience and post the matching escalation line. Returns the new level. */
export function annoy(k: string, lines: readonly string[], ch: string, kind: LogKind = "alert"): number {
  const n = (S.mood[k] ?? 0) + 1;
  S.mood[k] = n;
  post(ch, kind, k, lines[Math.min(n, lines.length) - 1] ?? "");
  return n;
}

/** "?", "!" and "…" bubbles above characters. */
export function drawBubbles(ctx: Ctx, now: number): void {
  for (const e of S.ents) {
    if (e.hidden) continue;
    const f = S.speaking && (e.id === S.speaking || e.k === S.speaking) ? "…" : S.flags[e.id];
    if (!f) continue;
    const bob = Math.floor(now / 350) % 2;
    const x = Math.round(e.fx * T) + 3;
    const y = Math.round(e.fy * T) - 16 - bob;
    ctx.fillStyle = "#1b1426";
    ctx.fillRect(x - 1, y - 1, 12, 10);
    ctx.fillRect(x + 2, y + 8, 5, 3);
    ctx.fillStyle = f === "?" ? "#ffd447" : f === "!" ? "#ff5a6a" : "#ffffff";
    ctx.fillRect(x, y, 10, 8);
    ctx.fillRect(x + 3, y + 8, 3, 2);
    ctx.fillStyle = "#1b1426";
    if (f === "!") {
      ctx.fillRect(x + 4, y + 1, 2, 4);
      ctx.fillRect(x + 4, y + 6, 2, 1);
    } else if (f === "?") {
      ctx.fillRect(x + 3, y + 1, 4, 1);
      ctx.fillRect(x + 6, y + 2, 1, 2);
      ctx.fillRect(x + 4, y + 4, 2, 1);
      ctx.fillRect(x + 4, y + 6, 2, 1);
    } else {
      const k = Math.floor(now / 250) % 4;
      for (let i = 0; i < 3; i++) if (i < k) ctx.fillRect(x + 2 + i * 3, y + 4, 1, 1);
    }
  }
}
